import { Row, Table, Modal, Form, Button } from "react-bootstrap";
import { AutoCol, Checkbox, ControlledDateInput, Icon } from "../../../component-library";
import { RecoveryFormData } from ".";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Supply } from "../../../models/recoveries/RecoveryAppFormData";
import { fas } from "@fortawesome/free-solid-svg-icons";

interface SuppliesListProps {
    formData: RecoveryFormData;
}

export function SuppliesList({ formData }: Readonly<SuppliesListProps>) {
    const { setValue } = useFormContext();
    const [activeSupplyIndex, setActiveSupplyIndex] = useState<number>(-1);
    const supplies = formData?.Supplies ?? [];
    const [showSupplyModal, setShowSupplyModal] = useState<boolean>(false);
    const [supplyInputs, setSupplyInputs] = useState<Partial<Supply>>({});

    const handleSupplyRowClick = (index: number) => {
        setActiveSupplyIndex(index);
        setSupplyInputs(supplies?.[index]);
        setShowSupplyModal(true);
    };

    const handleSupplyModalClose = () => {
        setShowSupplyModal(false);
        setActiveSupplyIndex(-1);
        setSupplyInputs({});
    };

    const handleNewSupply = () => {
        setActiveSupplyIndex(supplies.length);
        setShowSupplyModal(true);
    };

    const removeSupply = (index: number) => {
        const newSupplies = [...supplies];
        newSupplies.splice(index, 1);
        setValue("Supplies", newSupplies, { shouldDirty: true });
        handleSupplyModalClose();
    };

    const formatSupplyDate = (date: string | undefined) => {
        return date
            ? new Date(date).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
            : "";
    };

    const handleSaveSupply = () => {
        if (activeSupplyIndex === -1) return;
        setValue(`Supplies.${activeSupplyIndex}.Name`, supplyInputs.Name, { shouldDirty: true });
        setValue(`Supplies.${activeSupplyIndex}.LotNumber`, supplyInputs.LotNumber, { shouldDirty: true });
        setValue(`Supplies.${activeSupplyIndex}.Expiry`, formatSupplyDate(supplyInputs.Expiry), { shouldDirty: true });
        handleSupplyModalClose();
    };

    return (
        <>
            <h3 className="fw-bold">Supplies</h3>
            <Row>
                <AutoCol>
                    <Checkbox
                        accessor="RecoverySiteAdequate"
                        label="Prior to the surgical removal of eye tissue, the recovery site was adequate or was made adequate per ASN policy and procedure E1.050"
                    />
                </AutoCol>
            </Row>
            <Row>
                <AutoCol className="mb-3">
                    <Table hover className="clickable">
                        <tbody>
                            {supplies?.map((supply, i) => {
                                return (
                                    <tr key={`${supply.Name}-${i}`} onClick={() => handleSupplyRowClick(i)}>
                                        <td>{supply.Name}</td>
                                        <td>{supply.LotNumber}</td>
                                        <td>{supply.Expiry}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    <Button variant="primary" onClick={handleNewSupply} size="sm">
                        <Icon icon={fas.faPlus} /> New Supply
                    </Button>
                </AutoCol>
            </Row>

            <Modal show={showSupplyModal} onHide={handleSupplyModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{activeSupplyIndex === -1 ? "New Supply" : "Edit Supply"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="name" className="mb-2">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={supplyInputs.Name}
                            onChange={(e) => setSupplyInputs({ ...supplyInputs, Name: e.target.value })}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group controlId="lotNumber" className="mb-2">
                        <Form.Label>Lot Number</Form.Label>
                        <Form.Control
                            type="text"
                            value={supplyInputs.LotNumber}
                            onChange={(e) => setSupplyInputs({ ...supplyInputs, LotNumber: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group controlId="expiry" className="mb-2">
                        <Form.Label>Expiration Date</Form.Label>
                        <ControlledDateInput
                            value={supplyInputs.Expiry}
                            onChange={(e) => setSupplyInputs({ ...supplyInputs, Expiry: e?.toISOString() })}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={handleSupplyModalClose}>
                        Cancel
                    </Button>
                    <div>
                        {activeSupplyIndex > -1 && (
                            <Button variant="danger" onClick={() => removeSupply(activeSupplyIndex)} className="me-2">
                                Remove Supply
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleSaveSupply}>
                            Save
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}
